import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  announce,
  loadPrefs,
  savePrefs,
  setHighWater,
  highWater,
} from '../../lib/browserNotify';

/**
 * NotifyCenterBridge — one poller, every notification, one browser alert each.
 * (notify-center, 7 Sep 2026)
 *
 * This is the SIBLING of components/mail/MailNotifyBridge.jsx described in the
 * brief: it shares that bridge's polling cadence (POLL_MS below is the same
 * 30 s) and, where the mail bridge is present, that bridge hands its OS
 * notifications over to this one (claude-fixes/apply_notify_center_mailbridge.py
 * swaps its inline `new Notification(...)` block for the shared library) so a
 * mail never rings twice.  Mail still arrives here anyway, because
 * routes/mail_notify.py writes its rows into the same `notifications`
 * collection that /api/notify-center/feed reads.
 *
 * Mounted once from the page Header.  Module-level state makes a second mount
 * harmless: only the first live instance polls.
 */

export const POLL_MS = 30000;

const shared = {
  instances: 0,
  primary: null,
  since: null,
};

/** Other components (the panel) listen for this to redraw the bell. */
export const NOTIFY_EVENT = 'dcs:notify-center';

export function emitNotifyEvent(detail) {
  try {
    window.dispatchEvent(new CustomEvent(NOTIFY_EVENT, { detail }));
  } catch (e) { /* very old browser */ }
}

export default function NotifyCenterBridge() {
  const navigate = useNavigate();
  const auth = useAuth() || {};
  const user = auth.user;
  const userId = user?.id;
  const idRef = useRef(null);
  const [, setTick] = useState(0);

  const open = useCallback((item) => {
    if (!item) return;
    if (item.id && String(item.id).indexOf(':') === -1) {
      // Only rows that live in `notifications` have a server-side read flag.
      api.post(`/notifications/${item.id}/read`).catch(() => {});
    }
    navigate(item.link || '/ats/activity');
  }, [navigate]);

  useEffect(() => {
    if (!userId) return undefined;
    shared.instances += 1;
    const myId = shared.instances;
    idRef.current = myId;
    if (shared.primary === null) shared.primary = myId;
    let stopped = false;

    // A refresh must not replay: start from whichever is newer, the mark this
    // browser remembers or the mark the server remembers for this user.
    if (shared.since === null) shared.since = highWater(userId) || null;

    const tick = async () => {
      if (stopped || shared.primary !== myId) return;
      try {
        const params = shared.since ? { since: shared.since } : {};
        const r = await api.get('/notify-center/feed', { params });
        const data = r.data || {};
        const items = data.items || [];

        // Keep the local pref cache in step with the server copy.
        if (data.prefs) savePrefs(userId, { ...loadPrefs(userId), ...data.prefs });

        if (shared.since && items.length) {
          announce(items, {
            userId,
            prefs: loadPrefs(userId),
            onOpen: open,
          });
          emitNotifyEvent({ items, unread_count: data.unread_count });
          setTick((n) => n + 1);
        } else if (items.length) {
          // First tick after a cold start: record what exists, ring nothing.
          const newest = items.reduce(
            (acc, i) => (!acc || String(i.created_at) > String(acc) ? i.created_at : acc),
            null,
          );
          setHighWater(userId, newest);
          emitNotifyEvent({ items: [], unread_count: data.unread_count });
        }
        shared.since = data.now || new Date().toISOString();

        // Persist the high-water mark server-side too, so a new device does
        // not re-announce a week of history.
        api.post('/notify-center/seen', { last_seen_at: shared.since }).catch(() => {});
      } catch (e) {
        /* offline, logged out, or the module is not installed yet — retry */
      }
    };

    tick();
    const interval = setInterval(tick, POLL_MS);

    // A tab coming back to the foreground should catch up immediately.
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      if (shared.primary === myId) shared.primary = null;
    };
  }, [userId, open]);

  // If the primary unmounts (navigation), the next instance takes over.
  useEffect(() => {
    const handle = setInterval(() => {
      if (shared.primary === null && idRef.current !== null) shared.primary = idRef.current;
    }, 2000);
    return () => clearInterval(handle);
  }, []);

  return null;
}
